import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader } from "@/components/Loader";
import { Building2, Globe, Tag, Newspaper, Box, Mail } from "lucide-react";

/**
 * Public-facing firm dossier. NO authentication — anyone with the token can view.
 * URL: /p/firm/<token>
 * Used as the deliverable for auto-fulfilled research requests when the admin
 * checked "Also generate public pitch URL" — the client can forward this to prospects directly.
 */
const fmtMoney = (n) => {
    if (!n) return "—";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

export default function PublicFirm() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [firm, setFirm] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        const BASE = process.env.REACT_APP_BACKEND_URL;
        axios.get(`${BASE}/api/public/firm/${encodeURIComponent(token)}`)
            .then(({ data }) => setFirm(data))
            .catch((e) => {
                const d = e?.response?.data?.detail;
                setError(typeof d === "string" ? d : "Firm not found or revoked");
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return <div className="min-h-screen bg-black p-12"><Loader label="Loading dossier" /></div>;
    if (error) return (
        <div className="min-h-screen bg-black flex items-center justify-center p-12" data-testid="public-firm-error">
            <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-6 py-4 font-mono max-w-md text-center">{error}</div>
        </div>
    );
    if (!firm) return null;

    const facts = [
        ["Industry", firm.industry],
        ["Region", firm.region],
        ["HQ", firm.headquarters],
        ["Founded", firm.founded_year],
        ["Size", firm.company_size],
        ["Website", firm.website],
    ].filter(([, v]) => v);

    return (
        <div className="min-h-screen bg-black text-zinc-100" data-testid="public-firm-page">
            <div className="p-8 max-w-4xl mx-auto">
                <div className="mb-6 flex items-center justify-between print:hidden">
                    <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-500">
                        powered by <span className="text-amber-500">Affynix</span>
                    </Link>
                    <button
                        onClick={() => window.print()}
                        data-testid="public-firm-print-btn"
                        className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-1.5 transition-colors"
                    >
                        Save as PDF
                    </button>
                </div>

                {/* Cover */}
                <div className="border border-zinc-800 bg-zinc-950 p-8 mb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2 flex items-center gap-2">
                        <Building2 className="w-3 h-3" /> Firm dossier
                    </div>
                    <h1 className="font-heading font-black tracking-tighter text-5xl text-zinc-50 leading-none mb-4" data-testid="public-firm-name">
                        {firm.name}
                    </h1>
                    {firm.overview && <p className="text-base text-zinc-300 leading-relaxed">{firm.overview}</p>}
                </div>

                {facts.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <Globe className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Key facts</div>
                        </div>
                        <dl className="divide-y divide-zinc-800/60">
                            {facts.map(([k, v]) => (
                                <div key={k} className="px-5 py-2.5 grid grid-cols-3 gap-3">
                                    <dt className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{k}</dt>
                                    <dd className="col-span-2 text-sm text-zinc-200">{String(v)}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}

                {firm.products && firm.products.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <Box className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Products</div>
                        </div>
                        <ul className="divide-y divide-zinc-800/60">
                            {firm.products.map((p, i) => (
                                <li key={i} className="px-5 py-4">
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                                        <div className="font-heading font-bold text-zinc-50 text-base">{p.name}</div>
                                        {p.annual_revenue_usd ? <span className="font-mono text-xs text-emerald-400">{fmtMoney(p.annual_revenue_usd)}/yr</span> : null}
                                    </div>
                                    {p.description && <p className="text-sm text-zinc-300 leading-relaxed">{p.description}</p>}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {firm.competitors && firm.competitors.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6 px-5 py-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Competitors</div>
                        <div className="flex flex-wrap gap-1.5">
                            {firm.competitors.map((c, i) => (
                                <span key={i} className="font-mono text-xs border border-zinc-800 text-zinc-300 px-2 py-0.5">{c}</span>
                            ))}
                        </div>
                    </section>
                )}

                {firm.recent_news && firm.recent_news.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <Newspaper className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Recent news</div>
                        </div>
                        <ul className="divide-y divide-zinc-800/60">
                            {firm.recent_news.slice(0, 6).map((n, i) => (
                                <li key={i} className="px-5 py-2.5 text-sm text-zinc-200">{n}</li>
                            ))}
                        </ul>
                    </section>
                )}

                {firm.tags && firm.tags.length > 0 && (
                    <section className="border border-zinc-800 bg-zinc-950 mb-6 px-5 py-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 flex items-center gap-2">
                            <Tag className="w-3 h-3" /> Tags
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {firm.tags.map((t, i) => (
                                <span key={i} className="font-mono text-[10px] border border-amber-500/40 bg-amber-500/10 text-amber-300 px-2 py-0.5">{t}</span>
                            ))}
                        </div>
                    </section>
                )}

                {/* AI-generated disclaimer — same defensive notice as the public pitch page. */}
                <div
                    className="border border-zinc-800 bg-zinc-950/60 px-4 py-3 mt-8 flex items-start gap-2.5 print:hidden"
                    data-testid="ai-disclaimer"
                >
                    <span className="font-mono text-[10px] text-amber-500/80 mt-0.5 shrink-0">⚠</span>
                    <div className="text-[11px] text-zinc-500 leading-relaxed">
                        <span className="font-mono uppercase tracking-wider text-zinc-400">AI-generated dossier.</span>{" "}
                        Products, revenue estimates, and competitive claims are synthesized by Claude Sonnet 4.5 from
                        public signals — they are working estimates, not audited data. Verify specific numbers before
                        contracting.{" "}
                        <Link
                            to="/methodology"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="methodology-link"
                            className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline"
                        >
                            How we build dossiers →
                        </Link>
                    </div>
                </div>

                <div className="text-center font-mono text-[10px] text-zinc-700 mt-4 print:hidden">
                    Dossier shared via <Link to="/" className="text-amber-500 hover:text-amber-400">Affynix</Link>.
                </div>
            </div>

            {firm.contact_email && (
                <div className="sticky bottom-0 left-0 right-0 border-t border-emerald-500/40 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80 print:hidden" data-testid="sticky-contact-cta-firm">
                    <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-zinc-300 leading-snug min-w-0">
                            <span className="font-mono text-emerald-400 text-[10px] uppercase tracking-wider">Questions?</span>{" "}
                            <span className="text-zinc-400">Email us at <span className="text-zinc-200 font-mono">{firm.contact_email}</span></span>
                        </div>
                        <a
                            href={`mailto:${firm.contact_email}?subject=${encodeURIComponent(`Re: ${firm.name || ""} dossier`)}`}
                            data-testid="sticky-contact-link-firm"
                            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[11px] uppercase tracking-wider px-4 py-2 shrink-0"
                        >
                            <Mail className="w-3.5 h-3.5" /> Send email
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}
